import { useId } from "react";
import { cn } from "../lib/cn";

/** The single Tarii brand lockup: four-block symbol plus wordmark. */
export function Logo({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-7 shrink-0 select-none items-center gap-[0.5em] text-fg",
        className,
      )}
      role="img"
      aria-label="Tarii"
    >
      <img
        src="/tarii-mark-combined.svg"
        alt=""
        width="264"
        height="264"
        draggable={false}
        style={{ height: "100%", width: "auto", flexShrink: 0 }}
      />
      <LogoWordmark />
    </span>
  );
}

/**
 * The four-block symbol alone, square, sized by `className` (e.g. `h-5 w-5`).
 * Decorative unless `label` is given.
 *
 * `small` (for 24 px and under; the brand's small-size variant, approved by
 * the founder 12 Oct 2026): the full mark's 36 pale tiles turn into a grey
 * smudge on a white screen, so a miniature stands in — one block per panel
 * in that panel's key tone, on the mark's own light plate with its hairline.
 * It holds on white, on Telegram's dark themes and on any custom Telegram
 * colour. `public/favicon.svg` is the same drawing.
 */
export function LogoMark({
  className,
  label,
  small = false,
  testid,
}: {
  className?: string;
  label?: string;
  small?: boolean;
  testid?: string;
}) {
  if (small) {
    return (
      <svg
        viewBox="0 0 24 24"
        role={label ? "img" : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        data-testid={testid}
        data-mark="small"
        className={cn("shrink-0 select-none", className)}
      >
        <rect
          x="0.5"
          y="0.5"
          width="23"
          height="23"
          rx="5.5"
          fill="#F5F8F5"
          stroke="#ADB8B4"
          strokeWidth="0.75"
        />
        <rect x="4" y="4" width="7.2" height="7.2" rx="1.8" fill="#315A60" />
        <rect x="12.8" y="4" width="7.2" height="7.2" rx="1.8" fill="#9FCDBB" />
        <rect x="4" y="12.8" width="7.2" height="7.2" rx="1.8" fill="#BBDDCF" />
        <rect x="12.8" y="12.8" width="7.2" height="7.2" rx="1.8" fill="#2D4247" />
      </svg>
    );
  }
  return (
    <img
      src="/tarii-mark-combined.svg"
      alt={label ?? ""}
      aria-hidden={label ? undefined : true}
      width="264"
      height="264"
      draggable={false}
      data-testid={testid}
      data-mark="full"
      className={cn("shrink-0 select-none", className)}
    />
  );
}

// The two i-dots in tarii-logo.png (image units): drawn as their own circles
// when `liveDots` is set, so a page can colour and animate them.
const I_DOTS = [
  { cx: 454, cy: 131 },
  { cx: 500, cy: 101 },
] as const;

/**
 * The "tarii" wordmark alone, sized by `height` (the lockup uses 78% of its
 * mark). With `liveDots`, the dots are cut out of the image and drawn as two
 * `.logo-dot` circles instead.
 */
export function LogoWordmark({
  height = "78%",
  liveDots = false,
}: {
  height?: string;
  liveDots?: boolean;
}) {
  const maskId = useId();
  return (
    <svg
      viewBox="152 78 372 194"
      aria-hidden="true"
      style={{
        height,
        width: "auto",
        aspectRatio: "372 / 194",
        overflow: "visible",
        flexShrink: 0,
      }}
    >
      <defs>
        <mask
          id={maskId}
          x="0"
          y="0"
          width="677"
          height="369"
          maskUnits="userSpaceOnUse"
          style={{ maskType: "alpha" }}
        >
          <image href="/tarii-logo.png" width="677" height="369" />
        </mask>
      </defs>
      {liveDots ? (
        <>
          <g mask={`url(#${maskId})`}>
            {/* Everything but the dots' box, which the circles below replace. */}
            <path d="M0 0H677V369H0Z M430 78V154H524V78Z" fillRule="evenodd" fill="currentColor" />
          </g>
          {I_DOTS.map((dot) => (
            <circle key={dot.cx} className="logo-dot" cx={dot.cx} cy={dot.cy} r="13.5" />
          ))}
        </>
      ) : (
        <g mask={`url(#${maskId})`}>
          <rect width="677" height="369" fill="currentColor" />
          <rect x="430" y="78" width="94" height="76" fill="var(--logo-dot-color, #9fcdbb)" />
        </g>
      )}
    </svg>
  );
}
